import { Datapoints } from '../datapoints';
import deleteTaskService from '../services/deleteTaskService';
import updateTaskService from '../services/updateTaskService';
import TaskImportance from '../store/schema/TaskImportance';
import Todo from '../store/schema/Todo';
import convertRestIdToEwsId from '../utils/convertRestIdToEwsId';
import delay from '../utils/delay';
import getPartialTaskFromTodo from '../utils/getPartialTaskFromTodo';
import { logUsage } from 'owa-analytics';
import now from 'owa-datetime/lib/userDate/now';
import { addPopoutV2, getReadingPaneRouteForPopoutV2 } from 'owa-popout-v2';
import { orchestrator } from 'satcheljs';
import {
    moveFromActiveToCompletedList,
    removeTodoFromView,
    moveFromCompletedToActiveList,
} from '../actions/todoFolderViewActions';
import {
    clickMarkImportantContextMenuItem,
    clickRemoveImportanceContextMenuItem,
} from '../actions/todoContextMenuActions';
import {
    deleteTodo,
    markTodoComplete,
    markTodoIncomplete,
    updateTodo,
    star,
    todoUpdated,
    unstar,
    updateTodoDueDate,
    updateTodoReminder,
    updateTodoSubject,
    clickEmail,
} from '../actions/todoActions';

const COMPLETION_DELAY = 150; // 150 ms

orchestrator(clickEmail, async actionMessage => {
    const route = getReadingPaneRouteForPopoutV2(null, convertRestIdToEwsId(actionMessage.emailId));
    addPopoutV2('mail', route, null, {
        resizable: true,
    });
});

export const markTodoCompleteOrchestrator = orchestrator(markTodoComplete, async actionMessage => {
    const todoUpdates: Partial<Todo> = {
        Status: 'Completed',
        CompletedDateTime: now(),
    };
    updateTodo(actionMessage.todoId, todoUpdates);
    logUsage(
        Datapoints.todoMetaDataCompletedTask.datapoint.name,
        Datapoints.todoMetaDataCompletedTask.datapoint.customData(actionMessage.todoId),
        Datapoints.todoMetaDataCompletedTask.datapoint.options()
    );

    await delay(COMPLETION_DELAY);
    moveFromActiveToCompletedList(actionMessage.todoId);
});

export const markTodoInCompleteOrchestrator = orchestrator(
    markTodoIncomplete,
    async actionMessage => {
        const todoUpdates: Partial<Todo> = {
            Status: 'InProgress',
        };
        updateTodo(actionMessage.todoId, todoUpdates);

        await delay(COMPLETION_DELAY);
        moveFromCompletedToActiveList(actionMessage.todoId, 0);
    }
);

export const clickMarkImportantContextMenuItemOrchestrator = orchestrator(
    clickMarkImportantContextMenuItem,
    actionMessage => {
        updateTodoImportance(actionMessage.itemId, TaskImportance.High);
    }
);

export const clickRemoveImportanceContextMenuItemOrchestrator = orchestrator(
    clickRemoveImportanceContextMenuItem,
    actionMessage => {
        updateTodoImportance(actionMessage.itemId, TaskImportance.Normal);
    }
);

export const starOrchestrator = orchestrator(star, actionMessage => {
    updateTodoImportance(actionMessage.todoId, TaskImportance.High);
});

export const unstarOrchestrator = orchestrator(unstar, actionMessage => {
    updateTodoImportance(actionMessage.todoId, TaskImportance.Normal);
});

function updateTodoImportance(itemId: string, importance: TaskImportance) {
    const todoUpdates: Partial<Todo> = {
        Importance: importance,
    };
    updateTodo(itemId, todoUpdates);
}

export const updateSubjectOrchestrator = orchestrator(updateTodoSubject, actionMessage => {
    updateTodo(actionMessage.itemId, { Subject: actionMessage.subject });
});

export const updateTodoOrchestrator = orchestrator(updateTodo, async actionMessage => {
    const { todoUpdates, itemId } = actionMessage;
    const updates = getPartialTaskFromTodo(todoUpdates);

    if (!updates) {
        return null;
    }

    const isSuccess = await updateTaskService(updates, itemId);

    todoUpdated(isSuccess, itemId, todoUpdates);
});

export const deleteTodoOrchestrator = orchestrator(deleteTodo, async actionMessage => {
    removeTodoFromView(actionMessage.itemId);
    await deleteTaskService(actionMessage.itemId);
});

export const updateTodoReminderOrchestrator = orchestrator(
    updateTodoReminder,
    async actionMessage => {
        // TODO anmcmaho datapoint
        updateTodo(actionMessage.itemId, {
            ReminderDateTime: actionMessage.reminderDateTime,
            IsReminderOn: actionMessage.reminderDateTime != null,
        });
    }
);

export const updateTodoDueDateOrchestrator = orchestrator(
    updateTodoDueDate,
    async actionMessage => {
        updateTodo(actionMessage.itemId, {
            DueDateTime: actionMessage.dueDate,
        });
    }
);

export { Todo };
